// context/AuthContext.js
// MULTI-GARAGE ARCHITECTURE - Future-proof design
// Supports: timezone, admin handling, fast login path, session management
// Performance: Profile caching, parallel queries, reduced timeouts
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import { supabase } from "../config/supabaseClient";
import { debug } from "../Utils/debug";
import { getEffectivePermissions } from "../config/permissions";

const AuthContext = createContext();

// Session timeout constants (in milliseconds)
const SESSION_TIMEOUT_CLIENT = 90 * 24 * 60 * 60 * 1000; // 90 days
const SESSION_TIMEOUT_GARAGE = 1 * 24 * 60 * 60 * 1000;  // 1 day
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

// Performance: Query timeout and cache settings
const QUERY_TIMEOUT = 5000; // 5 seconds — fail fast on Edge/localhost where queries can hang
const PROFILE_CACHE_KEY = "cm_profile_cache";
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

// Profile cache helpers - avoid redundant DB queries on page refresh
const profileCache = {
  get: (userId) => {
    try {
      const cached = sessionStorage.getItem(`${PROFILE_CACHE_KEY}_${userId}`);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      // Check if cache is still valid
      if (Date.now() - timestamp < PROFILE_CACHE_TTL) {
        debug.auth("Profile cache HIT for user:", userId);
        return data;
      }
      // Cache expired
      sessionStorage.removeItem(`${PROFILE_CACHE_KEY}_${userId}`);
      return null;
    } catch {
      return null;
    }
  },

  set: (userId, data) => {
    try {
      sessionStorage.setItem(`${PROFILE_CACHE_KEY}_${userId}`, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      debug.auth("Profile cached for user:", userId);
    } catch {
      // sessionStorage full or unavailable
    }
  },

  clear: (userId) => {
    try {
      if (userId) {
        sessionStorage.removeItem(`${PROFILE_CACHE_KEY}_${userId}`);
      } else {
        // Clear all profile caches
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key?.startsWith(PROFILE_CACHE_KEY)) {
            sessionStorage.removeItem(key);
          }
        }
      }
    } catch {
      // Ignore
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  // Track if initial auth check is complete - prevents premature redirects
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  //  Ref to always have the latest user in async callbacks (avoids stale closure in onAuthStateChange)
  const userRef = useRef(null);
  // Keep ref in sync with state
  useEffect(() => { userRef.current = user; }, [user]);
  // Ref mirror for initialLoadComplete (avoids stale closure in onAuthStateChange listener)
  const initialLoadCompleteRef = useRef(false);
  useEffect(() => { initialLoadCompleteRef.current = initialLoadComplete; }, [initialLoadComplete]);

  // In-flight deduplication: reuse a pending loadUserProfile promise for the same user
  const profileLoadInFlight = useRef(null);
  // Ref to always have the latest logout function (avoids stale closure since logout is defined later)
  const logoutRef = useRef(null);

  // Check if session has expired based on user type
  const checkSessionExpiry = useCallback(async () => {
    if (!user || !sessionExpiry) return;

    const now = Date.now();
    if (now >= sessionExpiry) {
      debug.auth("Session expired, logging out user");
      if (logoutRef.current) await logoutRef.current();
      // Could show a toast notification here
      return true; // Session expired
    }
    return false; // Session still valid
  }, [user, sessionExpiry]);

  // Create or update user session in database
  const createUserSession = useCallback(async (userId, userType) => {
    try {
      const userAgent = navigator.userAgent;
      const sessionToken = crypto.randomUUID ? crypto.randomUUID() : `session_${Date.now()}`;

      // Parse user agent for device info
      const deviceType = /mobile/i.test(userAgent) ? "Mobile" :
                        /tablet|ipad/i.test(userAgent) ? "Tablet" : "Desktop";
      const browser = /chrome/i.test(userAgent) && !/edge/i.test(userAgent) ? "Chrome" :
                     /safari/i.test(userAgent) && !/chrome/i.test(userAgent) ? "Safari" :
                     /firefox/i.test(userAgent) ? "Firefox" :
                     /edge/i.test(userAgent) ? "Edge" : "Unknown";
      const os = /windows/i.test(userAgent) ? "Windows" :
                /mac os/i.test(userAgent) ? "MacOS" :
                /linux/i.test(userAgent) ? "Linux" :
                /android/i.test(userAgent) ? "Android" :
                /iphone|ipad|ipod/i.test(userAgent) ? "iOS" : "Unknown";

      // Calculate expiry based on user type
      const timeout = userType === "garage" ? SESSION_TIMEOUT_GARAGE : SESSION_TIMEOUT_CLIENT;
      const expiresAt = new Date(Date.now() + timeout);

      // Store session in database
      const { error } = await supabase.from("user_sessions").insert({
        user_id: userId,
        session_token: sessionToken,
        device_type: deviceType,
        browser: browser,
        os: os,
        is_current: true,
        last_active: new Date().toISOString(),
        expires_at: expiresAt.toISOString(),
      });

      if (error) {
        debug.error("Failed to create user session:", error);
      } else {
        debug.auth("User session created, expires:", expiresAt);
        setSessionExpiry(expiresAt.getTime());
        // Session token stored in DB only — not in localStorage (XSS risk)
      }

      return sessionToken;
    } catch (err) {
      debug.error("Error creating user session:", err);
      return null;
    }
  }, []);

  // Send login alert email for new logins
  const sendLoginAlert = useCallback(async (userId, email, firstName) => {
    try {
      const userAgent = navigator.userAgent;
      const deviceType = /mobile/i.test(userAgent) ? "Mobile" :
                        /tablet|ipad/i.test(userAgent) ? "Tablet" : "Desktop";
      const browser = /chrome/i.test(userAgent) && !/edge/i.test(userAgent) ? "Chrome" :
                     /safari/i.test(userAgent) && !/chrome/i.test(userAgent) ? "Safari" :
                     /firefox/i.test(userAgent) ? "Firefox" :
                     /edge/i.test(userAgent) ? "Edge" : "Unknown";

      await supabase.functions.invoke("send-auth-email", {
        body: {
          email,
          template_type: "new_login_alert",
          variables: {
            first_name: firstName || email.split("@")[0],
            city: "Unknown",
            country: "Unknown",
            device_type: deviceType,
            browser: browser,
            login_time: new Date().toLocaleString(),
          },
        },
      });

      debug.auth("Login alert email sent");
    } catch (err) {
      debug.error("Failed to send login alert:", err);
      // Don't throw - this is non-critical
    }
  }, []);

  // Initialize and subscribe to auth state changes
  // Strategy: Let onAuthStateChange be the SINGLE source of truth.
  // checkUser() only runs as a fallback if INITIAL_SESSION hasn't fired within 2s.
  useEffect(() => {
    let isMounted = true;
    let initialSessionHandled = false;

    // Fallback: if onAuthStateChange hasn't fired INITIAL_SESSION within 2s, run checkUser
    const fallbackTimer = setTimeout(async () => {
      if (!initialSessionHandled && isMounted) {
        debug.auth("INITIAL_SESSION not received within 2s, running checkUser fallback");
        await checkUser();
        if (isMounted) {
          setInitialLoadComplete(true);
        }
      }
    }, 2000);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      debug.auth("Auth event:", event);

      // Only process auth changes if component is still mounted
      if (!isMounted) return;

      // Mark that the auth listener has fired — cancel checkUser fallback
      if (event === "INITIAL_SESSION") {
        initialSessionHandled = true;
        clearTimeout(fallbackTimer);
      }

      if (session?.user) {
        debug.auth("Session user email:", session.user.email);

        // TOKEN_REFRESHED events should NOT re-fetch the profile.
        // Re-fetching risks a timeout that would wipe the good profile data.
        if (event === "TOKEN_REFRESHED") {
          debug.auth("Token refreshed - keeping existing user profile (no re-fetch)");
          return;
        }

        // For SIGNED_IN / INITIAL_SESSION events, check if this is a duplicate for the same user
        // (Supabase fires multiple auth events during login — only load profile once)
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          // Use a function to read the latest user state (avoid stale closure)
          const currentUser = await new Promise(resolve => {
            setUser(prev => {
              resolve(prev);
              return prev; // don't change state
            });
          });

          if (currentUser && currentUser.id === session.user.id && currentUser.currentGarageId) {
            debug.auth("Duplicate", event, "for same user with valid profile - skipping re-fetch");
            if (!initialLoadCompleteRef.current) setInitialLoadComplete(true);
            return;
          }
          if (currentUser && currentUser.id === session.user.id && currentUser.clientId) {
            debug.auth("Duplicate", event, "for same user with valid client profile - skipping re-fetch");
            if (!initialLoadCompleteRef.current) setInitialLoadComplete(true);
            return;
          }
          if (currentUser && currentUser.id === session.user.id && currentUser.type === "admin") {
            debug.auth("Duplicate", event, "for same admin user - skipping re-fetch");
            if (!initialLoadCompleteRef.current) setInitialLoadComplete(true);
            return;
          }
        }

        await loadUserProfile(session.user);
      } else {
        // Only clear user if this is a real sign-out event, not a transient state
        if (event === "SIGNED_OUT" || event === "TOKEN_REFRESHED") {
          // On any auth callback page, SIGNED_OUT is a transient event during OAuth flow.
          // Ignore it entirely — SIGNED_IN will arrive shortly.
          // Covers both /auth/callback (PKCE) and /auth/google/callback (Google OAuth).
          const pathname = window.location.pathname;
          if (pathname.includes("/auth/callback") || pathname.includes("/auth/google/")) {
            debug.auth("SIGNED_OUT on callback page - ignoring transient OAuth event");
            return; // Skip setLoading(false) and setInitialLoadComplete(true)
          }
          // Don't clear state if a profile load is actively in-flight
          // (Supabase fires transient SIGNED_OUT during OAuth callback)
          if (profileLoadInFlight.current) {
            debug.auth("SIGNED_OUT during active profile load - ignoring transient event");
          } else {
            // Double-check: verify session is truly gone before clearing
            // This prevents transient SIGNED_OUT events during OAuth flows
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (currentSession) {
              debug.auth("SIGNED_OUT event but session still exists - ignoring transient event");
            } else {
              debug.auth("No session user - clearing auth state");
              setUser(null);
              setSessionExpiry(null);
            }
          }
        } else {
          debug.auth("No session but event is:", event, "- not clearing user yet");
          // On the auth callback page, INITIAL_SESSION fires with no session
          // BEFORE the PKCE code exchange completes and SIGNED_IN fires.
          // Don't finalize loading here — let SIGNED_IN handle it.
          // Otherwise ProtectedRoute sees user=null + loading=false and redirects to login.
          if (event === "INITIAL_SESSION" && (window.location.pathname.includes("/auth/callback") || window.location.pathname.includes("/auth/google/"))) {
            debug.auth("On callback page - keeping loading=true until SIGNED_IN arrives");
            return; // Skip setLoading(false) and setInitialLoadComplete(true)
          }
        }
      }
      setLoading(false);
      if (!initialLoadCompleteRef.current && isMounted) {
        setInitialLoadComplete(true);
      }
    });

    // Cross-tab logout synchronization
    // Listen for storage events to detect logout in other tabs
    const handleStorageChange = (event) => {
      // Detect logout broadcast from another tab
      if (event.key === "logout_broadcast" && event.newValue) {
        debug.auth("Logout detected in another tab, clearing local state");
        setUser(null);
        setSessionExpiry(null);
        setLoading(false);
        // Redirect to home page
        window.location.href = "/";
      }

      // Detect if auth storage was cleared in another tab
      if (event.key === null && event.oldValue === null) {
        debug.auth("Storage cleared in another tab, verifying session");
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (!session && isMounted) {
            debug.auth("No valid session, clearing user state");
            setUser(null);
            setSessionExpiry(null);
          }
        }).catch(() => {
          // Session check failed (network error, etc.) - don't clear state
        });
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimer);
      subscription.unsubscribe();
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Periodic session expiry check
  useEffect(() => {
    if (!user || !sessionExpiry) return;

    const interval = setInterval(() => {
      checkSessionExpiry();
    }, SESSION_CHECK_INTERVAL);

    // Also check immediately when session expiry changes
    checkSessionExpiry();

    return () => clearInterval(interval);
  }, [user, sessionExpiry, checkSessionExpiry]);

  // Session expiry is set during createUserSession and loadUserProfile.
  // No localStorage persistence needed — Supabase manages session continuity.

  // Check existing session on mount
  const checkUser = async () => {
    try {
      // Skip if the auth listener already loaded a valid profile
      const existing = userRef.current;
      if (existing && (existing.clientId || existing.currentGarageId || existing.type === "admin")) {
        debug.auth("checkUser: profile already loaded by auth listener, skipping");
        return;
      }

      debug.auth("Checking existing user session...");
      setLoading(true);
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        debug.auth("Found existing session:", session.user.email);
        await loadUserProfile(session.user);
      } else {
        debug.auth("No existing session found");
      }
    } catch (error) {
      debug.error("Error checking user:", error);
    } finally {
      debug.auth("checkUser complete, setting loading to false");
      setLoading(false);
    }
  };

  // Load and shape user profile into context
  const loadUserProfile = async (authUser) => {
    // Deduplicate: if a loadUserProfile call is already in-flight for this user, reuse it
    if (profileLoadInFlight.current && profileLoadInFlight.current.userId === authUser.id) {
      debug.auth("loadUserProfile already in-flight for:", authUser.email, "— reusing existing promise");
      return profileLoadInFlight.current.promise;
    }

    const doLoad = async () => {
    try {
      debug.auth("loadUserProfile START for:", authUser.email);
      debug.auth("Auth user ID:", authUser.id);

      // Check user type from metadata FIRST (fast path)
      // Also check localStorage as fallback (set during admin login before metadata propagates)
      // localStorage persists across tabs (unlike sessionStorage) so admin preview tabs work
      const metadataType = authUser.user_metadata?.user_type;
      const storedType = localStorage.getItem("userType");
      const userType = metadataType || (storedType === "admin" ? "admin" : null);
      debug.auth("User type from metadata:", metadataType, "localStorage:", storedType, "resolved:", userType);

      // If user type is explicitly "client" or "garage", skip admin check entirely
      if (userType === "client") {
        debug.auth("Fast path: User is a client, skipping admin check");
        await loadClientProfile(authUser);
        return;
      }

      if (userType === "garage") {
        debug.auth("Fast path: User is a garage, skipping admin check");
        await loadGarageProfile(authUser);
        return;
      }

      if (userType === "employee") {
        debug.auth("Fast path: User is an employee, loading employee profile");
        await loadEmployeeProfile(authUser);
        return;
      }

      // Convert DB snake_case roles to Title Case for UI compatibility
      const adminRoleLabels = {
        super_admin: "Super Admin",
        bd_manager: "BD Manager",
        bd_rep: "BD Rep",
        support_admin: "Support Admin",
        guest_admin: "Guest Admin",
      };

      // Fast path for admin: check sessionStorage first, then query as backup
      if (userType === "admin") {
        debug.auth("Fast path: User metadata says admin, loading admin profile");
        const storedAdmin = sessionStorage.getItem("adminUser");
        if (storedAdmin) {
          try {
            const parsed = JSON.parse(storedAdmin);
            debug.auth("Using cached admin data from sessionStorage");

            const dbRole = parsed.role || "guest_admin";
            const isGuestAdmin = dbRole === "guest_admin" || dbRole === "Guest Admin";
            const adminUserObj = {
              id: authUser.id,
              email: parsed.email || authUser.email,
              type: "admin",
              role: adminRoleLabels[dbRole] || dbRole,
              fullName: parsed.fullName || authUser.email.split("@")[0],
              permissions: isGuestAdmin ? ["read"] : ["all"],
              isActive: true,
              isGuestAdmin: isGuestAdmin,
              canEdit: !isGuestAdmin,
            };

            setUser(adminUserObj);
            debug.auth("Admin user set from sessionStorage!");

            // Background refresh from DB (non-blocking)
            const expectedUserId = authUser.id;
            supabase
              .from("admin_users")
              .select("*")
              .eq("user_id", authUser.id)
              .maybeSingle()
              .then(({ data: freshAdmin }) => {
                // Guard: only update if same user is still logged in
                if (freshAdmin && userRef.current?.id === expectedUserId) {
                  const freshRole = freshAdmin.role || "guest_admin";
                  const freshIsGuest = freshRole === "guest_admin";
                  setUser({
                    id: authUser.id,
                    email: freshAdmin.email || authUser.email,
                    type: "admin",
                    role: adminRoleLabels[freshRole] || freshRole,
                    fullName: `${freshAdmin.first_name || ""} ${freshAdmin.last_name || ""}`.trim() || authUser.email.split("@")[0],
                    permissions: freshIsGuest ? ["read"] : ["all"],
                    isActive: freshAdmin.is_active !== false,
                    isGuestAdmin: freshIsGuest,
                    canEdit: !freshIsGuest,
                  });
                  // Update sessionStorage
                  sessionStorage.setItem("adminUser", JSON.stringify({
                    id: authUser.id,
                    email: freshAdmin.email || authUser.email,
                    type: "admin",
                    role: freshRole,
                    fullName: `${freshAdmin.first_name || ""} ${freshAdmin.last_name || ""}`.trim() || authUser.email.split("@")[0],
                  }));
                  debug.auth("Admin data refreshed from DB");
                }
              })
              .catch((err) => debug.auth("Background admin refresh failed:", err.message));

            return;
          } catch (parseErr) {
            debug.auth("Failed to parse stored admin data, falling through to DB query");
          }
        }
        // No localStorage data, fall through to DB query below
      }

      // Only check admin_users table if user_type is NOT set or is "admin"
      debug.auth("Checking admin_users table (user_type not set or admin)...");

      let adminUser = null;
      let adminError = null;

      try {
        // Timeout for admin query (8s to allow for Supabase cold starts)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("Admin query timeout after 8s")),
            8000
          )
        );

        const queryPromise = supabase
          .from("admin_users")
          .select("*")
          .eq("user_id", authUser.id)
          .maybeSingle();

        const result = await Promise.race([queryPromise, timeoutPromise]);

        adminUser = result.data;
        adminError = result.error;

        debug.auth("Admin query completed:", { adminUser, adminError });
      } catch (queryErr) {
        debug.error("Admin query error:", queryErr.message);
        adminError = queryErr;
      }

      // If user is in admin_users table, they're an admin
      if (adminUser && !adminError) {
        debug.auth("User IS an admin!");
        debug.auth("Admin data:", adminUser);

        // Determine if user is read-only (Guest Admin)
        // Database stores roles in snake_case: 'guest_admin', 'super_admin', etc.
        const isGuestAdmin = adminUser.role === "guest_admin";
        const canEdit = !isGuestAdmin;

        const dbRole = adminUser.role || "guest_admin";
        const adminUserObj = {
          id: authUser.id,
          email: adminUser.email || authUser.email,
          type: "admin",
          role: adminRoleLabels[dbRole] || dbRole,
          fullName: `${adminUser.first_name || ""} ${adminUser.last_name || ""}`.trim() || authUser.email.split("@")[0],
          permissions: isGuestAdmin ? ["read"] : ["all"],
          isActive: adminUser.is_active !== false,
          // Guest Admin specific flags
          isGuestAdmin: isGuestAdmin,
          canEdit: canEdit,
        };

        debug.auth("Setting admin user object:", adminUserObj);
        setUser(adminUserObj);
        debug.auth("Admin user set successfully!");
        return;
      }

      // If admin query had an error or timed out, check if we KNOW they're admin
      if (adminError) {
        debug.auth("Admin query failed:", adminError.message);
        // If user_type metadata says admin, preserve admin status instead of falling back to client
        if (userType === "admin") {
          debug.auth("User metadata says admin — keeping admin type despite query failure");
          const fallbackAdmin = {
            id: authUser.id,
            email: authUser.email,
            type: "admin",
            role: "Guest Admin",
            fullName: authUser.email.split("@")[0],
            permissions: ["read"],
            isActive: true,
            isGuestAdmin: true,
            canEdit: false,
          };
          setUser(fallbackAdmin);
          return;
        }
      }

      // Default to client if no user type set
      debug.auth("No user type in metadata, defaulting to client");
      await loadClientProfile(authUser);

    } catch (error) {
      debug.error("CRITICAL ERROR in loadUserProfile:", error);
      debug.error("Error details:", error.message);
      debug.error("Error code:", error.code);

      // Set a fallback user to prevent infinite loading
      debug.auth("Setting fallback client user due to error");
      setUser({
        id: authUser.id,
        email: authUser.email,
        type: "client",
        timezone: "America/Toronto",
      });
    } finally {
      debug.auth("loadUserProfile COMPLETE");
      profileLoadInFlight.current = null;
    }
    };

    const promise = doLoad();
    profileLoadInFlight.current = { userId: authUser.id, promise };
    return promise;
  };

  // Load client profile (extracted for reuse)
  // Performance: Uses sessionStorage cache to avoid redundant queries
  const loadClientProfile = async (authUser) => {
    debug.auth("Loading client user profile...");

    // Check cache first for faster page loads
    // BUT don't use cache if clientId is null (might be stale from before client was created)
    const cachedProfile = profileCache.get(authUser.id);
    if (cachedProfile && cachedProfile.type === "client" && cachedProfile.clientId) {
      debug.auth("Using cached client profile with clientId:", cachedProfile.clientId);
      setUser(cachedProfile);
      return;
    }

    // If cache has null clientId, clear it and re-query
    if (cachedProfile && !cachedProfile.clientId) {
      debug.auth("Cached profile has null clientId, clearing cache and re-querying...");
      profileCache.clear(authUser.id);
    }

    let clientProfile = null;
    let clientError = null;

    try {
      // Performance: Reduced timeout from 10s to 5s
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Client query timeout")), QUERY_TIMEOUT)
      );

      const queryPromise = supabase
        .from("clients")
        .select("*")
        .eq("user_id", authUser.id)
        .maybeSingle();

      const result = await Promise.race([queryPromise, timeoutPromise]);
      clientProfile = result.data;
      clientError = result.error;
      debug.auth("Client query completed");
    } catch (queryErr) {
      // Log timeouts as warnings since we handle them gracefully
      if (queryErr.message?.includes("timeout")) {
        debug.auth("Client query timed out, will try email fallback");
      } else {
        debug.error("Client query error:", queryErr.message);
      }
      clientError = queryErr;
    }

    // Email fallback: runs OUTSIDE try/catch so it executes even after timeouts.
    // SECURITY: Never auto-link unowned accounts — only match already-linked records
    if (!clientProfile && authUser.email) {
      try {
        debug.auth("No client found by user_id, checking by email for existing link...");
        const emailTimeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Email fallback timeout")), QUERY_TIMEOUT)
        );
        const emailQuery = supabase
          .from("clients")
          .select("*")
          .eq("email", authUser.email)
          .maybeSingle();
        const { data: clientByEmail } = await Promise.race([emailQuery, emailTimeout]);

        if (clientByEmail && clientByEmail.user_id === authUser.id) {
          // Already linked to this user — safe to use
          clientProfile = clientByEmail;
          debug.auth("Found client by email fallback:", clientByEmail.id);
        }
        // If clientByEmail exists but user_id differs, do NOT auto-link.
      } catch (emailErr) {
        debug.auth("Email fallback failed:", emailErr.message);
      }
    }

    // Only log actual database errors, not timeouts or "no rows" responses
    if (clientError && clientError.code && clientError.code !== "PGRST116") {
      debug.error("Error loading client profile:", clientError);
    }

    // If no client profile found, check if we already have a valid one in state
    if (!clientProfile) {
      const currentUser = await new Promise(resolve => {
        setUser(prev => {
          resolve(prev);
          return prev;
        });
      });

      if (currentUser && currentUser.id === authUser.id && currentUser.clientId) {
        debug.auth("No client row found but existing profile is valid - keeping current state");
        return;
      }

      // Set user immediately with null clientId so navigation isn't blocked.
      // Schedule a background retry — the query may have timed out due to
      // Edge Tracking Prevention or network issues on localhost.
      debug.auth("Client not found, setting user with null clientId (will retry in background)");
    }

    // Log client profile resolution for debugging social login issues
    debug.auth("Client profile found:", clientProfile ? "YES" : "NO");
    debug.auth("Client profile ID:", clientProfile?.id || "none");
    debug.auth("Client profile user_id:", clientProfile?.user_id || "none");
    debug.auth("Client profile email:", clientProfile?.email || "none");
    debug.auth("Auth user ID:", authUser.id);
    debug.auth("Auth user email:", authUser.email);

    const clientUserObj = {
      id: authUser.id,
      email: authUser.email,
      type: "client",
      clientId: clientProfile?.id || null,
      firstName: clientProfile?.first_name || "",
      lastName: clientProfile?.last_name || "",
      phone: clientProfile?.phone || "",
      address: clientProfile?.address || "",
      city: clientProfile?.city || "",
      province: clientProfile?.province || "",
      state: clientProfile?.state || "",
      postalCode: clientProfile?.postal_code || "",
      country: clientProfile?.country || "CA",
      timezone: clientProfile?.timezone || "America/Toronto",
    };

    // Only cache profiles with a valid clientId — don't cache timed-out/empty results
    if (clientUserObj.clientId) {
      profileCache.set(authUser.id, clientUserObj);
    } else {
      debug.auth("Client profile has null clientId - NOT caching");
    }

    debug.auth("Setting client user with clientId:", clientUserObj.clientId);
    debug.auth("Client timezone:", clientProfile?.timezone);
    setUser(clientUserObj);
    debug.auth("Client user set successfully!");

    // Background retry: if we got null clientId (query timed out), retry once after 3s.
    // This handles Edge Tracking Prevention on localhost where initial queries hang
    // but subsequent ones may succeed once the browser warms up.
    if (!clientUserObj.clientId) {
      (async () => {
        for (let attempt = 1; attempt <= 2; attempt++) {
          const delay = attempt * 2000; // 2s, 4s
          await new Promise(resolve => setTimeout(resolve, delay));
          try {
            debug.auth(`Background retry ${attempt}/2 for client profile...`);
            const retryTimeout = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Retry timeout")), QUERY_TIMEOUT)
            );
            // Try by user_id first
            const retryQuery = supabase
              .from("clients")
              .select("*")
              .eq("user_id", authUser.id)
              .maybeSingle();
            const { data: retryProfile } = await Promise.race([retryQuery, retryTimeout]);

            // If not found by user_id, try email fallback
            let foundProfile = retryProfile;
            if (!foundProfile && authUser.email) {
              const emailTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error("Email retry timeout")), QUERY_TIMEOUT)
              );
              const emailQuery = supabase
                .from("clients")
                .select("*")
                .eq("email", authUser.email)
                .maybeSingle();
              const { data: emailProfile } = await Promise.race([emailQuery, emailTimeout]);
              if (emailProfile?.user_id === authUser.id) {
                foundProfile = emailProfile;
              }
            }

            if (foundProfile?.id) {
              debug.auth(`Background retry ${attempt}: client found!`, foundProfile.id);
              const updatedUser = {
                ...clientUserObj,
                clientId: foundProfile.id,
                firstName: foundProfile.first_name || "",
                lastName: foundProfile.last_name || "",
                phone: foundProfile.phone || "",
                timezone: foundProfile.timezone || "America/Toronto",
              };
              profileCache.set(authUser.id, updatedUser);
              setUser(updatedUser);
              return; // Success — stop retrying
            }
          } catch (e) {
            debug.auth(`Background retry ${attempt} failed:`, e.message);
          }
        }
        debug.auth("Background retries exhausted, clientId remains null");
      })();
    }
  };

  // Load garage profile (extracted for reuse)
  // Performance: Uses sessionStorage cache to avoid redundant queries
  const loadGarageProfile = async (authUser) => {
    debug.auth("Loading garage profile...");

    // Check cache first for faster page loads
    // BUT don't use cache if currentGarageId is null (might be stale from a failed query)
    const cachedProfile = profileCache.get(authUser.id);
    if (cachedProfile && cachedProfile.type === "garage" && cachedProfile.currentGarageId) {
      debug.auth("Using cached garage profile with garageId:", cachedProfile.currentGarageId);
      setUser(cachedProfile);
      return;
    }

    // If cache has null currentGarageId, clear it and re-query
    if (cachedProfile && cachedProfile.type === "garage" && !cachedProfile.currentGarageId) {
      debug.auth("Cached garage profile has null currentGarageId, clearing cache and re-querying...");
      profileCache.clear(authUser.id);
    }

    let garages = null;
    let error = null;

    try {
      // Performance: Reduced timeout from 10s to 5s
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Garage query timeout")), QUERY_TIMEOUT)
      );

      const queryPromise = supabase
        .from("garages")
        .select("*")
        .eq("user_id", authUser.id)
        .order("created_at", { ascending: true });

      const result = await Promise.race([queryPromise, timeoutPromise]);
      garages = result.data;
      error = result.error;
      debug.auth("Garage query completed");

      // If no garages found by user_id, check by email for already-linked accounts only
      // SECURITY: Never auto-link unowned accounts — only match already-linked records
      if ((!garages || garages.length === 0) && authUser.email) {
        debug.auth("No garages found by user_id, checking by email_1 for existing link...");
        const { data: garagesByEmail } = await supabase
          .from("garages")
          .select("*")
          .eq("email_1", authUser.email)
          .order("created_at", { ascending: true });

        if (garagesByEmail && garagesByEmail.length > 0) {
          // Only use records that are already linked to this user
          const linkedGarages = garagesByEmail.filter(g => g.user_id === authUser.id);
          if (linkedGarages.length > 0) {
            garages = linkedGarages;
          }
          // If garages exist but user_id differs, do NOT auto-link.
          // The user must complete registration or contact support to claim ownership.
        }
      }
    } catch (queryErr) {
      // Log timeouts as warnings since we handle them gracefully
      if (queryErr.message?.includes("timeout")) {
        debug.auth("Garage query timed out, clearing cache to prevent stale data");
        profileCache.clear(authUser.id);
      } else {
        debug.error("Garage query error:", queryErr.message);
        profileCache.clear(authUser.id);
      }
      error = queryErr;
    }

    // Only log actual database errors, not timeouts or "no rows" responses
    if (error && error.code && error.code !== "PGRST116") {
      debug.error("Error loading garage profiles:", error);
    }

    // SAFETY NET: If the query failed (timeout/error) and we already have a valid
    // garage user in state, don't overwrite it with broken data.
    // This prevents token refresh timeouts from wiping a working session.
    if (error && (!garages || garages.length === 0)) {
      // Read current user state to check if we already have good data
      const currentUser = await new Promise(resolve => {
        setUser(prev => {
          resolve(prev);
          return prev; // don't change state
        });
      });

      if (currentUser && currentUser.id === authUser.id && currentUser.currentGarageId) {
        debug.auth("Garage query failed but existing profile is valid - keeping current state");
        debug.auth("Preserved garageId:", currentUser.currentGarageId);
        return;
      }
    }

    // Select the first garage (or previously selected garage from sessionStorage)
    const savedGarageId = localStorage.getItem("currentGarageId");
    let currentGarage = null;

    if (garages && garages.length > 0) {
      currentGarage =
        garages.find((g) => g.id === savedGarageId) || garages[0];
    }

    debug.auth("Loaded garages:", garages?.length || 0);
    debug.auth("Current garage:", currentGarage?.business_name);

    // If no garage record exists yet, mark as new registration so the dashboard
    // can redirect to profile setup (address confirmation on map)
    const isNewReg = !currentGarage && authUser.user_metadata?.user_type === "garage";

    const garageUserObj = {
      id: authUser.id,
      email: authUser.email,
      type: "garage",
      garages: garages || [],
      currentGarageId: currentGarage?.id || null,
      isNewRegistration: isNewReg || false,
      businessName: currentGarage?.business_name || authUser.user_metadata?.business_name || "",
      phone: currentGarage?.phone || "",
      address: currentGarage?.address || "",
      city: currentGarage?.city || "",
      province: currentGarage?.province || authUser.user_metadata?.province || "",
      state: currentGarage?.state || authUser.user_metadata?.state || "",
      postalCode: currentGarage?.postal_code || "",
      country: currentGarage?.country || authUser.user_metadata?.country || "CA",
      timezone: currentGarage?.timezone || authUser.user_metadata?.timezone || "America/Toronto",
      hourlyRate: currentGarage?.hourly_rate || 95,
      services: currentGarage?.services || {},
      operatingHours: currentGarage?.operating_hours || {},
      logo: currentGarage?.logo || null,
      taxConfig: currentGarage?.tax_config || null,
      subscriptionTier: currentGarage?.subscription || "Free",
      // Role within the garage hierarchy (owner, manager, technician, accounting)
      // Default to "owner" for new registrations (no garage record yet) and garage creators.
      // For employees and unknown users, default to jockey (most restrictive).
      garageRole: currentGarage?.user_role || (isNewReg || currentGarage?.user_id === authUser.id ? "owner" : "jockey"),
    };

    // Only cache the profile if it has a valid currentGarageId
    // Don't cache empty/null profiles - they'd poison future loads
    if (garageUserObj.currentGarageId) {
      profileCache.set(authUser.id, garageUserObj);
      debug.auth("Garage profile cached with garageId:", garageUserObj.currentGarageId);
    } else {
      debug.auth("Garage profile has null currentGarageId - NOT caching");
      profileCache.clear(authUser.id);

      //  SAFETY NET: If we have NO garage but the current user in state
      // already has a valid garage profile for the same auth ID, keep it.
      // This prevents a timed-out query from wiping an active session.
      const existingUser = userRef.current;
      if (existingUser?.type === "garage" && existingUser?.currentGarageId && existingUser?.id === authUser.id) {
        debug.auth("SAFETY NET: Keeping existing garage profile instead of setting empty one");
        debug.auth("Existing garageId:", existingUser.currentGarageId);
        return; // Don't overwrite the good user object
      }
    }

    setUser(garageUserObj);
    debug.auth("Garage user set successfully!");
    debug.auth("Garage timezone:", currentGarage?.timezone);
  };

  // Load profile for employee users (technicians, receptionists, etc.)
  const loadEmployeeProfile = async (authUser) => {
    try {
      debug.auth("Loading employee profile for:", authUser.email);

      // Query garage_employees with joined garage data
      const { data: employeeRecords, error } = await supabase
        .from("garage_employees")
        .select("*, garages(*)")
        .eq("user_id", authUser.id)
        .eq("is_active", true);

      if (error || !employeeRecords || employeeRecords.length === 0) {
        debug.auth("No active employee records found, falling back to client");
        await loadClientProfile(authUser);
        return;
      }

      debug.auth(`Found ${employeeRecords.length} employee record(s)`);

      // Pick the current garage (support multi-garage employees)
      const savedGarageId = localStorage.getItem("currentGarageId");
      const currentEmployee = employeeRecords.find(e => e.garage_id === savedGarageId)
                              || employeeRecords[0];
      const garage = currentEmployee.garages;

      if (!garage) {
        debug.auth("Employee record has no linked garage, falling back to client");
        await loadClientProfile(authUser);
        return;
      }

      // Build the user object - type "garage" so ProtectedRoute works
      const employeeUserObj = {
        id: authUser.id,
        email: authUser.email,
        type: "garage",
        garages: employeeRecords.map(e => ({
          ...e.garages,
          employeeRole: e.role,
          employeeId: e.id,
          employeeName: e.name,
          customPermissions: e.custom_permissions,
          technician_id: e.technician_id,
        })),
        currentGarageId: garage.id,
        businessName: garage.business_name || "",
        phone: garage.phone || "",
        address: garage.address || "",
        city: garage.city || "",
        province: garage.province || "",
        state: garage.state || "",
        postalCode: garage.postal_code || "",
        country: garage.country || "CA",
        timezone: garage.timezone || "America/Toronto",
        hourlyRate: garage.hourly_rate || 95,
        services: garage.services || {},
        operatingHours: garage.operating_hours || {},
        logo: garage.logo || null,
        taxConfig: garage.tax_config || null,
        subscriptionTier: garage.subscription || "Free",
        // Employee-specific fields
        garageRole: currentEmployee.role,
        isEmployee: true,
        employeeId: currentEmployee.id,
        employeeName: currentEmployee.name,
        permissions: getEffectivePermissions(currentEmployee.role, currentEmployee.custom_permissions),
        customPermissions: currentEmployee.custom_permissions,
        technician_id: currentEmployee.technician_id,
      };

      debug.auth("Employee profile loaded:", {
        garageRole: employeeUserObj.garageRole,
        isEmployee: true,
        garageName: employeeUserObj.businessName,
      });

      setUser(employeeUserObj);
    } catch (err) {
      debug.auth("Error loading employee profile:", err.message);
      await loadClientProfile(authUser);
    }
  };

  // Switch between garages (for multi-garage feature)
  const switchGarage = async (garageId) => {
    if (!user || !user.garages) return;

    const garage = user.garages.find((g) => g.id === garageId);
    if (!garage) return;

    localStorage.setItem("currentGarageId", garageId);

    const updatedUser = {
      ...user,
      currentGarageId: garage.id,
      businessName: garage.business_name || "",
      phone: garage.phone || "",
      address: garage.address || "",
      city: garage.city || "",
      province: garage.province || "",
      state: garage.state || "",
      postalCode: garage.postal_code || "",
      country: garage.country || "CA",
      timezone: garage.timezone || "America/Toronto",
      hourlyRate: garage.hourly_rate || 95,
      services: garage.services || {},
      operatingHours: garage.operating_hours || {},
      logo: garage.logo || null,
      taxConfig: garage.tax_config || null,
      subscriptionTier: garage.subscription || "Free",
    };

    // Update employee-specific fields when switching garages
    if (user.isEmployee && garage.employeeRole) {
      updatedUser.garageRole = garage.employeeRole;
      updatedUser.employeeId = garage.employeeId;
      updatedUser.employeeName = garage.employeeName;
      updatedUser.permissions = getEffectivePermissions(garage.employeeRole, garage.customPermissions);
      updatedUser.customPermissions = garage.customPermissions;
      updatedUser.technician_id = garage.technician_id;
    }

    setUser(updatedUser);
  };

  // Robust register: ensure session, create garage/client profile, load context
  const register = async (email, password, userData) => {
    try {
      setLoading(true);

      debug.auth("=== REGISTRATION START ===");
      debug.auth("Email:", email);
      debug.auth("User type:", userData?.type);
      debug.auth("Timezone:", userData?.timezone);
      debug.auth("Country:", userData?.country);

      const { data: authData, error: signUpError } = await supabase.auth.signUp(
        {
          email,
          password,
          options: {
            data: {
              user_type: userData.type,
              // Store registration data in user metadata for garage users
              // Garage record will be created in step 2 (GarageProfile.js)
              business_name: userData.businessName || null,
              country: userData.country || "CA",
              province: userData.province || null,
              state: userData.state || null,
              timezone: userData.timezone || "America/Toronto",
            },
          },
        }
      );

      if (signUpError) {
        debug.error("Signup error:", signUpError);
        return { success: false, error: signUpError.message };
      }
      debug.auth("Auth signup successful");

      let {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        debug.auth("No session after signUp; signing in explicitly...");
        const { data: signInData, error: signInErr } =
          await supabase.auth.signInWithPassword({ email, password });
        if (signInErr) {
          debug.error("Sign-in after signup failed:", signInErr);
          return { success: false, error: signInErr.message };
        }
        session = signInData.session;
        debug.auth("Explicit sign-in established session");
      }

      const sessionUser = session?.user || authData?.user;
      if (!sessionUser) {
        debug.error("No user object available after signup/signin.");
        return { success: false, error: "No user available after sign up." };
      }

      // Create profile with timezone fields
      if (userData.type === "garage") {
        // NOTE: Garage record is NOT created here anymore.
        // It will be created in GarageProfile.js (Step 2) after address validation.
        // Registration data is stored in auth user metadata above.
        debug.auth("Garage registration step 1 complete. Garage record will be created in step 2 (address validation).");
        debug.auth("Stored in user metadata - businessName:", userData.businessName, "timezone:", userData.timezone);

        // Set temporary user context for GarageProfile.js to access
        setUser({
          id: sessionUser.id,
          email: sessionUser.email,
          type: "garage",
          businessName: userData.businessName || "",
          country: userData.country || "CA",
          province: userData.province || null,
          state: userData.state || null,
          timezone: userData.timezone || "America/Toronto",
          isNewRegistration: true, // Flag to indicate garage record doesn't exist yet
        });

        return { success: true, user: sessionUser };
      } else if (userData.type === "client") {
        debug.auth("Creating client profile with timezone...");
        const { error: profileError } = await supabase.from("clients").insert([
          {
            user_id: sessionUser.id,
            email,
            first_name: userData.firstName || "",
            last_name: userData.lastName || "",
            phone: userData.phone || "",
            address: userData.address || "",
            city: userData.city || "",
            province: userData.province || null,
            state: userData.state || null,
            postal_code: userData.postalCode || "",
            country: userData.country || "CA",
            timezone: userData.timezone || "America/Toronto",
            created_at: new Date().toISOString(),
          },
        ]);

        if (profileError) {
          debug.error("Client profile creation failed:", profileError);
          return { success: false, error: "Failed to create client profile" };
        }
        debug.auth("Client profile created with timezone:", userData.timezone);
      }

      await loadUserProfile(sessionUser);
      return { success: true, user: sessionUser };
    } catch (error) {
      debug.error("Registration failed:", error);
      return {
        success: false,
        error: error.message || "Registration failed",
      };
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, options = {}) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      await loadUserProfile(data.user);

      // Get user type from metadata for session creation
      const userType = data.user.user_metadata?.user_type || "client";

      // Create user session in database (non-blocking)
      createUserSession(data.user.id, userType).catch((err) => {
        debug.error("Session creation failed:", err);
      });

      // Send login alert email if enabled (non-blocking)
      // Skip if this is a 2FA login (already handled) or explicitly disabled
      if (!options.skip2FAAlert && !options.skipLoginAlert) {
        const firstName = data.user.user_metadata?.first_name ||
                         data.user.user_metadata?.business_name ||
                         email.split("@")[0];
        sendLoginAlert(data.user.id, email, firstName).catch((err) => {
          debug.error("Login alert failed:", err);
        });
      }

      return { success: true };
    } catch (error) {
      debug.error("Login error:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      debug.auth("=== LOGOUT START ===");

      // 1. Immediately clear user state — this triggers ProtectedRoute redirect to login
      // Do NOT set loading=true here, so the redirect happens instantly
      const previousUser = user;
      setUser(null);
      setSessionExpiry(null);
      setLoading(false);
      debug.auth("User state cleared immediately");

      // 2. Clear all browser storage first (synchronous, instant)
      profileCache.clear();

      // localStorage - preserve cookie consent, OAuth state, language, and intro popup dismissals
      const cookieConsent = localStorage.getItem("cookieConsent");
      const oauthState = localStorage.getItem("google_oauth_state");
      const i18nextLng = localStorage.getItem("i18nextLng");
      const introKeys = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("intro_")) {
          introKeys[key] = localStorage.getItem(key);
        }
      }
      localStorage.clear();
      if (cookieConsent) localStorage.setItem("cookieConsent", cookieConsent);
      if (oauthState) localStorage.setItem("google_oauth_state", oauthState);
      if (i18nextLng) localStorage.setItem("i18nextLng", i18nextLng);
      Object.entries(introKeys).forEach(([k, v]) => localStorage.setItem(k, v));

      // sessionStorage
      sessionStorage.clear();
      debug.auth("All browser storage cleared");

      // 3. Broadcast logout to other tabs
      try {
        localStorage.setItem("logout_broadcast", Date.now().toString());
        localStorage.removeItem("logout_broadcast");
      } catch (e) { /* ignore */ }

      // 4. Everything below is non-blocking cleanup — don't await these
      // Sign out from Supabase (local scope = instant, no API call)
      // Use local scope to avoid hanging on slow connections
      supabase.auth.signOut({ scope: "local" }).catch((err) => {
        debug.error("Supabase local signOut error:", err);
      });

      // Invalidate database sessions in background (non-blocking)
      if (previousUser?.id) {
        supabase
          .from("user_sessions")
          .update({ is_current: false, is_revoked: true })
          .eq("user_id", previousUser.id)
          .eq("is_current", true)
          .then(() => debug.auth("Database sessions invalidated"))
          .catch((err) => debug.error("Session invalidation error:", err));
      }

      // Remove realtime channels in background
      try {
        const channels = supabase.getChannels();
        for (const channel of channels) {
          supabase.removeChannel(channel).catch(() => {});
        }
      } catch (channelError) {
        debug.error("Error removing channels:", channelError);
      }

      // Clear IndexedDB in background
      try {
        const databases = await window.indexedDB.databases();
        for (const db of databases) {
          if (db.name) window.indexedDB.deleteDatabase(db.name);
        }
      } catch (idbError) {
        try { window.indexedDB.deleteDatabase("supabase-auth"); } catch (e) { /* skip */ }
      }

      // Clear Service Worker caches
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: "CLEAR_CACHE" });
      }

      debug.auth("=== LOGOUT COMPLETE ===");
      return { success: true };
    } catch (error) {
      debug.error("Logout error:", error);
      // Even on error, ensure user state is cleared
      setUser(null);
      setSessionExpiry(null);
      setLoading(false);
      // Preserve cookie consent, OAuth state, language, and intro popup dismissals during cleanup
      const cookieConsent = localStorage.getItem("cookieConsent");
      const oauthState = localStorage.getItem("google_oauth_state");
      const i18nextLng = localStorage.getItem("i18nextLng");
      const introKeys = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("intro_")) {
          introKeys[key] = localStorage.getItem(key);
        }
      }
      localStorage.clear();
      if (cookieConsent) localStorage.setItem("cookieConsent", cookieConsent);
      if (oauthState) localStorage.setItem("google_oauth_state", oauthState);
      if (i18nextLng) localStorage.setItem("i18nextLng", i18nextLng);
      Object.entries(introKeys).forEach(([k, v]) => localStorage.setItem(k, v));
      sessionStorage.clear();
      return { success: false, error: error.message };
    }
  };

  // Keep logoutRef in sync so checkSessionExpiry can call it (avoids forward reference)
  logoutRef.current = logout;

  // Refresh subscription tier from database (called on dashboard load)
  // This ensures the cached tier stays in sync when admin changes it
  const refreshSubscriptionTier = async () => {
    if (!user || user.type !== "garage" || !user.currentGarageId) return;
    try {
      const { data, error } = await supabase
        .from("garages")
        .select("subscription")
        .eq("id", user.currentGarageId)
        .single();

      if (error || !data) return;

      const freshTier = data.subscription || "Free";
      if (freshTier !== user.subscriptionTier) {
        debug.auth("Subscription tier changed:", user.subscriptionTier, "â†’", freshTier);
        const updatedUser = { ...user, subscriptionTier: freshTier };
        setUser(updatedUser);
        profileCache.set(user.id, updatedUser);
      }
    } catch (err) {
      debug.error("Failed to refresh subscription tier:", err.message);
    }
  };

  // Support updating timezone fields
  const updateUser = async (updates) => {
    try {
      if (!user) return { success: false, error: "No user logged in" };

      if (user.type === "garage" && user.currentGarageId) {
        const dbUpdates = {};
        if (updates.businessName !== undefined)
          dbUpdates.business_name = updates.businessName;
        if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
        if (updates.address !== undefined) dbUpdates.address = updates.address;
        if (updates.city !== undefined) dbUpdates.city = updates.city;
        if (updates.province !== undefined)
          dbUpdates.province = updates.province;
        if (updates.state !== undefined)
          dbUpdates.state = updates.state;
        if (updates.postalCode !== undefined)
          dbUpdates.postal_code = updates.postalCode;
        if (updates.country !== undefined)
          dbUpdates.country = updates.country;
        if (updates.timezone !== undefined)
          dbUpdates.timezone = updates.timezone;
        if (updates.hourlyRate !== undefined)
          dbUpdates.hourly_rate = updates.hourlyRate;
        if (updates.services !== undefined)
          dbUpdates.services = updates.services;
        if (updates.operatingHours !== undefined)
          dbUpdates.operating_hours = updates.operatingHours;
        if (updates.logo !== undefined) dbUpdates.logo = updates.logo;
        if (updates.taxConfig !== undefined)
          dbUpdates.tax_config = updates.taxConfig;

        const { error } = await supabase
          .from("garages")
          .update(dbUpdates)
          .eq("id", user.currentGarageId);

        if (error) throw error;

        // Re-check user after async call (could be null if logout happened during await)
        if (!user) return { success: false, error: "User logged out during update" };

        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        // Invalidate cache so next load gets fresh data
        profileCache.set(user.id, updatedUser);
      } else if (user.type === "client") {
        const dbUpdates = {};
        if (updates.firstName !== undefined)
          dbUpdates.first_name = updates.firstName;
        if (updates.lastName !== undefined)
          dbUpdates.last_name = updates.lastName;
        if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
        if (updates.address !== undefined) dbUpdates.address = updates.address;
        if (updates.city !== undefined) dbUpdates.city = updates.city;
        if (updates.province !== undefined)
          dbUpdates.province = updates.province;
        if (updates.state !== undefined) dbUpdates.state = updates.state;
        if (updates.postalCode !== undefined)
          dbUpdates.postal_code = updates.postalCode;
        if (updates.country !== undefined) dbUpdates.country = updates.country;
        if (updates.timezone !== undefined)
          dbUpdates.timezone = updates.timezone;

        const { error } = await supabase
          .from("clients")
          .update(dbUpdates)
          .eq("user_id", user.id);

        if (error) throw error;

        // Re-check user after async call (could be null if logout happened during await)
        if (!user) return { success: false, error: "User logged out during update" };

        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        // Update cache with new data
        profileCache.set(user.id, updatedUser);
      }

      return { success: true };
    } catch (error) {
      debug.error("Update error:", error);
      return { success: false, error: error.message };
    }
  };

  const isAuthenticated = () => user !== null;
  const isUserType = (type) => user?.type === type;

  // Complete registration for social auth users (Google, etc.)
  // The user is already authenticated via social provider, just need to create profile
  const completeSocialRegistration = async (userData) => {
    try {
      setLoading(true);

      debug.auth("=== SOCIAL REGISTRATION COMPLETION START ===");
      debug.auth("User type:", userData?.type);
      debug.auth("Timezone:", userData?.timezone);

      // Get current session (user is already signed in via social auth)
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError || !session) {
        debug.error("No session found for social registration:", sessionError);
        return { success: false, error: "No active session. Please sign in again." };
      }

      const sessionUser = session.user;
      debug.auth("Social user ID:", sessionUser.id);
      debug.auth("Social user email:", sessionUser.email);

      // Create profile based on type
      if (userData.type === "garage") {
        // Update user metadata for garage
        await supabase.auth.updateUser({
          data: {
            user_type: "garage",
            business_name: userData.businessName || null,
            country: userData.country || "CA",
            province: userData.province || null,
            state: userData.state || null,
            timezone: userData.timezone || "America/Toronto",
          },
        });

        debug.auth("Garage social registration step 1 complete. Garage record will be created in step 2.");

        // Set temporary user context for GarageProfile.js
        setUser({
          id: sessionUser.id,
          email: sessionUser.email,
          type: "garage",
          businessName: userData.businessName || "",
          country: userData.country || "CA",
          province: userData.province || null,
          state: userData.state || null,
          timezone: userData.timezone || "America/Toronto",
          isNewRegistration: true,
          isSocialAuth: true,
        });

        return { success: true, user: sessionUser };
      } else if (userData.type === "client") {
        debug.auth("Creating client profile for social auth user...");

        const { error: profileError } = await supabase.from("clients").insert([
          {
            user_id: sessionUser.id,
            email: sessionUser.email,
            first_name: userData.firstName || "",
            last_name: userData.lastName || "",
            phone: userData.phone || "",
            address: userData.address || "",
            city: userData.city || "",
            province: userData.province || null,
            state: userData.state || null,
            postal_code: userData.postalCode || "",
            country: userData.country || "CA",
            timezone: userData.timezone || "America/Toronto",
            created_at: new Date().toISOString(),
          },
        ]);

        if (profileError) {
          debug.error("Client profile creation failed:", profileError);
          return { success: false, error: "Failed to create client profile" };
        }

        // Update user metadata
        await supabase.auth.updateUser({
          data: {
            user_type: "client",
          },
        });

        debug.auth("Client profile created for social auth user");
      }

      await loadUserProfile(sessionUser);
      return { success: true, user: sessionUser };
    } catch (error) {
      debug.error("Social registration completion failed:", error);
      return {
        success: false,
        error: error.message || "Social registration failed",
      };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    completeSocialRegistration,
    loading,
    setLoading,
    isAuthenticated,
    isUserType,
    updateUser,
    refreshSubscriptionTier,
    switchGarage,
    // Session management
    sessionExpiry,
    createUserSession,
    sendLoginAlert,
    // Profile loading (for Login.js to await before navigating)
    loadUserProfile,
    // Navigation stability
    initialLoadComplete,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;